/**
 * Drop storefront Sentry events for Axios "Network Error" on read-only page loads.
 *
 * Phones often lose the connection or background the tab while product, category,
 * banner, and cart lookups are in flight. Those GETs fail with no HTTP response
 * (status 0). The same moment usually takes the page's analytics calls down too,
 * and the API still returned 200 for the requests that actually arrived.
 *
 * An event is dropped only when all of the following are true:
 * - It is an AxiosError with no HTTP response, and code ERR_NETWORK or message "Network Error".
 * - The method is GET or HEAD (a read-only page-load fetch).
 * - The request URL is known and is not a checkout, payment, or cart-mutation path.
 * - The document itself is not the checkout page.
 *
 * Missing method, missing URL, or a mixed set of failed requests is kept.
 * The Axios call is read from hint.originalException. When that config is missing,
 * http breadcrumbs with status 0 are the fallback. event.request is the document
 * URL (used to detect the checkout page), not the Axios URL: treating it as the
 * failed call would hide POST failures that happen on ordinary pages.
 *
 * ignoreErrors cannot see the method or URL, so "Network Error" is not ignored
 * globally. Mutations and payment calls keep reporting.
 */

const READ_ONLY_METHODS = new Set(['get', 'head'])

// Substrings the checkout and payment flows use. Matched case-insensitively.
// Hyphens and underscores are ignored so apple-pay / google_pay stay reported.
const SENSITIVE_URL_PARTS = ['payment', 'braintree', 'paypal', 'applepay', 'venmo', 'googlepay', 'order', 'shipping']

const SENSITIVE_PATHS = ['/controllers/bigcommerce/shipping', '/controllers/bigcommerce/sync/carts']

/**
 * @param {object} event Sentry event
 * @param {{ originalException?: unknown }} [hint]
 * @returns {boolean} true when the event should be discarded
 */
export function shouldDropPageLoadNetworkError(event, hint) {
	if (!event || typeof event !== 'object') return false
	if (isCheckoutDocument(event)) return false

	let origin
	try {
		origin = inspectOriginalException(hint?.originalException)
	} catch {
		return false
	}

	// A present original exception that is not this Axios failure wins over a
	// lookalike message on the event. Non-Axios errors and HTTP responses stay.
	if (origin.seen && !origin.isAxiosNetworkError) return false
	if (origin.hasHttpResponse) return false
	if (eventHasHttpResponse(event)) return false

	const identified = origin.isAxiosNetworkError || (!origin.seen && exceptionValuesSayAxiosNetwork(event))
	if (!identified) return false

	const requests = requestsFor(origin, event)
	if (!requests.length) return false

	return requests.every((request) => isReadOnlyMethod(request.method) && hasUrl(request.url) && !isSensitiveRequestUrl(request.url))
}

function inspectOriginalException(error) {
	if (error == null) {
		return { seen: false, isAxiosNetworkError: false, hasHttpResponse: false, configRequest: null }
	}
	if (typeof error !== 'object') {
		return { seen: true, isAxiosNetworkError: false, hasHttpResponse: false, configRequest: null }
	}

	const name = typeof error.name === 'string' ? error.name : ''
	const isAxios = error.isAxiosError === true || name === 'AxiosError'
	const hasHttpResponse = error.response != null
	const code = typeof error.code === 'string' ? error.code : ''
	const message = typeof error.message === 'string' ? error.message.trim() : ''
	const config = error.config && typeof error.config === 'object' ? error.config : null

	return {
		seen: true,
		isAxiosNetworkError: isAxios && !hasHttpResponse && (code === 'ERR_NETWORK' || message === 'Network Error'),
		hasHttpResponse,
		configRequest: config ? { method: normalizeMethod(config.method), url: combineUrl(config.baseURL, config.url) } : null,
	}
}

function exceptionValuesSayAxiosNetwork(event) {
	return exceptionValues(event).some((entry) => {
		const type = typeof entry?.type === 'string' ? entry.type : ''
		const message = typeof entry?.value === 'string' ? entry.value.trim() : ''
		if (type !== 'AxiosError') return false
		return message === 'Network Error' || message === 'ERR_NETWORK' || /\bERR_NETWORK\b/.test(message)
	})
}

function exceptionValues(event) {
	const values = event?.exception?.values
	if (Array.isArray(values)) return values
	if (values && typeof values === 'object') return [values]
	return []
}

function eventHasHttpResponse(event) {
	const contextStatus = httpStatus(event?.contexts?.response?.status_code ?? event?.contexts?.response?.status)
	// 4xx/5xx only. A 2xx on the event is the document, not the failed XHR.
	if (contextStatus != null && contextStatus >= 400) return true

	const extraStatus = httpStatus(event?.extra?.responseStatus ?? event?.extra?.httpStatus ?? event?.extra?.status)
	if (extraStatus != null) return true

	return exceptionValues(event).some((entry) => {
		const message = typeof entry?.value === 'string' ? entry.value : ''
		return /status code\s+[1-5]\d\d/i.test(message)
	})
}

function httpStatus(value) {
	const status = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
	if (!Number.isInteger(status) || status < 100 || status >= 600) return null
	return status
}

function requestsFor(origin, event) {
	const configRequest = origin.configRequest
	const hasConfig = configRequest && (configRequest.method || hasUrl(configRequest.url))
	if (hasConfig && configRequest.method && hasUrl(configRequest.url)) return [configRequest]

	const failed = failedHttpRequests(event)
	if (hasConfig && configRequest.method && !isReadOnlyMethod(configRequest.method)) return [configRequest]

	if (failed.length && failed.every((request) => request.method && hasUrl(request.url))) {
		return failed.map((request) => ({
			method: (hasConfig && configRequest.method) || request.method,
			url: (hasConfig && configRequest.url) || request.url,
		}))
	}

	if (hasConfig) return [configRequest]
	return failed
}

function failedHttpRequests(event) {
	return breadcrumbList(event)
		.filter(isHttpBreadcrumb)
		.map((crumb) => {
			const data = crumb.data && typeof crumb.data === 'object' ? crumb.data : {}
			const status = Number(data.status_code ?? data.statusCode ?? data.status)
			return {
				status: Number.isFinite(status) ? status : null,
				method: normalizeMethod(data.method),
				url: typeof data.url === 'string' ? data.url : undefined,
			}
		})
		.filter((request) => request.status === 0)
}

function isHttpBreadcrumb(crumb) {
	const category = typeof crumb?.category === 'string' ? crumb.category : ''
	const type = typeof crumb?.type === 'string' ? crumb.type : ''
	return category === 'xhr' || category === 'fetch' || type === 'http'
}

function breadcrumbList(event) {
	const crumbs = event?.breadcrumbs
	if (Array.isArray(crumbs)) return crumbs
	if (Array.isArray(crumbs?.values)) return crumbs.values
	return []
}

function isCheckoutDocument(event) {
	const candidates = [event?.request?.url, event?.transaction, event?.tags?.transaction, event?.tags?.url]
	const navigations = breadcrumbList(event).filter((crumb) => crumb?.category === 'navigation')
	const latest = navigations.length ? navigations[navigations.length - 1] : null
	if (typeof latest?.data?.to === 'string') candidates.push(latest.data.to)
	return candidates.some((value) => locationIsCheckout(value))
}

function locationIsCheckout(value) {
	if (typeof value !== 'string' || !value.trim()) return false
	const candidate = value.trim().replace(/^(GET|POST|PUT|PATCH|DELETE|HEAD)\s+/i, '')
	if (/^\/?checkout([/?#]|$)/i.test(candidate)) return true

	const path = pathnameOf(candidate)
	if (path === '/checkout' || path.startsWith('/checkout/')) return true

	const lower = candidate.toLowerCase()
	return lower.includes('/checkout/') || lower.endsWith('/checkout') || lower.includes('/checkout?') || lower.includes('/checkout#')
}

function pathnameOf(value) {
	try {
		if (/^https?:\/\//i.test(value)) return new URL(value).pathname.toLowerCase()
		if (value.startsWith('/')) return new URL(value, 'https://store.invalid').pathname.toLowerCase()
	} catch {
		return ''
	}
	return ''
}

function isSensitiveRequestUrl(url) {
	const value = String(url).toLowerCase()
	if (locationIsCheckout(value)) return true
	const collapsed = value.replace(/[-_]/g, '')
	if (SENSITIVE_URL_PARTS.some((part) => value.includes(part) || collapsed.includes(part))) return true
	return SENSITIVE_PATHS.some((part) => value.includes(part))
}

function isReadOnlyMethod(method) {
	return READ_ONLY_METHODS.has(method)
}

function hasUrl(url) {
	return typeof url === 'string' && url.length > 0
}

function normalizeMethod(method) {
	if (typeof method !== 'string' || !method.trim()) return undefined
	return method.trim().toLowerCase()
}

function combineUrl(baseURL, url) {
	if (typeof url !== 'string' || !url) return undefined
	if (/^https?:\/\//i.test(url) || typeof baseURL !== 'string' || !baseURL) return url
	return `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`
}
